import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import api from '../services/api';
import { queryClient } from '../queryClient';

export type AccountType = 'farmer' | 'employee';

export interface UserProfile {
    // HOF-101 (codex round 1, P1) — these are the keys `/auth/me` ACTUALLY
    // returns (`backend/src/api/v1/endpoints/auth.py::get_me`). The interface
    // declared an `id` the response has never carried; nothing consumed it, so
    // the fiction survived unnoticed until something did, and read `undefined`.
    user_id: string;
    // The TENANT — `get_effective_user_id`: an employee's inviter (the farm
    // owner), a farmer's own id. Anything scoping data (or a browser-local
    // preference about it) wants this and not `user_id`.
    effective_user_id?: string | null;
    phone: string;
    // #2008d: optional second login identity + its verification timestamp
    // (from the extended /auth/me). email_verified_at is null when the address is
    // present-but-unverified or absent.
    email?: string | null;
    email_verified_at?: string | null;
    account_type: AccountType;
    inviter_id?: string;
    inviter_name?: string;
    first_name?: string;
    last_name?: string;
    farm_name?: string;
    // #3031: delegated integration capabilities. The two lists answer different
    // questions and the difference is the whole point:
    //   integration_capabilities         — the LIVE grant state (from the backend)
    //   session_integration_capabilities — the snapshot this BFF session was minted
    //                                      with, i.e. what the email gate enforces
    // They diverge exactly while a grant is waiting for the grantee to re-login.
    // Both optional: a BFF/backend that predates #3031 sends neither.
    integration_capabilities?: string[];
    session_integration_capabilities?: string[];
    // HOF-627 (codex round 29): the BFF session's seat snapshot as its integration gate reads it —
    // null for a session minted before the snapshot (the legacy rule then applies).
    session_farm_permissions?: string[] | null;
    // HOF-627 §4: the farm this person is working in (their seat's role and permissions) and
    // every farm they belong to. A UI hint for what to offer — the backend decides.
    active_farm?: ActiveFarm | null;
    farms?: FarmSummary[];
    // HOF-696 D12: no active seat because a worker seat ended — the SPA lands on
    // "Bitte im Büro melden". Optional (an older backend omits it); read only through
    // `utils/landing.ts::isLabourAccessEnded`, which treats anything but `true` as false.
    labour_access_ended?: boolean;
    // HOF-708: mid-impersonation, the person being viewed (the access session's subject) — what
    // Settings → Profil shows. The identity fields above stay the admin's own. `null` outside an
    // impersonation; absent from a backend that predates it.
    impersonated_person?: ImpersonatedPerson | null;
}

export interface ImpersonatedPerson {
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    email: string | null;
    email_verified_at: string | null;
}

export interface FarmSummary {
    tenant_id: string;
    farm_name: string | null;
    role: string;
}

export interface ActiveFarm extends FarmSummary {
    permissions: string[];
    // HOF-696 D6: the apps this farm has switched on (`farm_apps`), next to the
    // seat's permissions. Optional: a backend from before HOF-696 omits it, and
    // every farm-app gate then fails closed (`utils/landing.ts::canOpenFarmApp`).
    apps?: string[];
    // HOF-697: the twin person a labour seat is bound to (null for an office seat). The worker
    // shell reads "my day" by it — a Vorarbeiter's seat may read the whole team's rows.
    person_id?: string | null;
}

export interface ImpersonationState {
    active: boolean;
    user_id?: string;
    farm_name?: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    hasPasskey: boolean;
    userPhone: string | null;
    userRoles: string[];
    userProfile: UserProfile | null;
    impersonation: ImpersonationState | null;
    // HOF-696 D5: both login paths resolve to the profile they fetched (null when
    // the read failed), so the caller lands on `landingFor(profile)`
    // instead of a render-time closure over the previous (empty) profile.
    login: (phoneNumber: string, otp: string) => Promise<UserProfile | null>;
    loginWithPasskey: () => Promise<UserProfile | null>;
    logout: () => Promise<void>;
    refreshToken: () => Promise<void>;
    checkPasskeys: () => Promise<void>;
    fetchUserRoles: () => Promise<void>;
    fetchUserProfile: () => Promise<UserProfile | null>;
    stopImpersonation: () => Promise<void>;
}

// Exported (#2954d) so a component can read the profile OPTIONALLY, via
// `useContext`, instead of through `useAuth` — which throws outside a provider.
// An app section that only wants the farm's name for a share card must not
// make every renderer of the page mount an AuthProvider, and must not crash a
// whole dashboard because one caption has no source.
export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasPasskey, setHasPasskey] = useState(false);
    const [userPhone, setUserPhone] = useState<string | null>(null);
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);

    /**
     * HOF-101 (codex fix round, P1) — `null` here means UNKNOWN, not
     * "not impersonating", and the two must stay distinguishable: a consumer
     * that reads the absence as a negative will treat an impersonated view as
     * the operator's own. So a successful read is normalised to an explicit
     * boolean, and only a FAILED one leaves it null.
     */
    const fetchImpersonationStatus = useCallback(async () => {
        try {
            const response = await api.get('/admin/impersonation-status', { withCredentials: true });
            const data = response.data ?? {};
            setImpersonation({
                active: Boolean(data.active),
                user_id: data.user_id ?? undefined,
                farm_name: data.farm_name ?? undefined,
            });
        } catch {
            // Endpoint unavailable → we do not KNOW. Never a silent "no".
            setImpersonation(null);
        }
    }, []);

    const stopImpersonation = useCallback(async () => {
        try {
            await api.post('/admin/stop-impersonate', {}, { withCredentials: true });
            // Clear cached roles — they reflect the impersonated user, not the admin.
            // Without this, getUserRoles() short-circuits and never re-fetches from API.
            localStorage.removeItem('user_roles');
            // Full page reload — skip React state updates to avoid re-render crashes
            // (no ErrorBoundary) from aborting in-flight module/resource loads.
            window.location.href = '/admin';
        } catch (error) {
            console.error('Failed to stop impersonation:', error);
        }
    }, []);

    const fetchUserProfile = useCallback(async (): Promise<UserProfile | null> => {
        if (authService.isAuthenticated()) {
            try {
                const response = await api.get('/auth/me', { withCredentials: true });
                const profile: UserProfile | null = response.data ?? null;
                setUserProfile(profile);
                return profile;
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
                setUserProfile(null);
                return null;
            }
        }
        setUserProfile(null);
        return null;
    }, []);

    const fetchUserRoles = useCallback(async () => {
        if (authService.isAuthenticated()) {
            const roles = await authService.getUserRoles();
            setUserRoles(roles);
            // Refresh phone from localStorage (may have been updated by getUserRoles)
            setUserPhone(authService.getUserPhoneDisplay());
        } else {
            setUserRoles([]);
        }
    }, []);

    const checkPasskeys = useCallback(async () => {
        if (authService.isAuthenticated()) {
            const hasPasskeys = await authService.hasPasskeys();
            setHasPasskey(hasPasskeys);
        } else {
            setHasPasskey(false);
        }
    }, []);

    // Check authentication status on mount
    useEffect(() => {
        const checkAuth = async () => {
            let authenticated = authService.isAuthenticated();

            // Hard-refresh rehydration (#1008): the access token lives only
            // ~15 min (token_expiry mirrors it), but the refresh session lives
            // 10 days. On a cold start the route guards decide auth state from
            // this flag synchronously, BEFORE any API call can trigger the
            // 401-interceptor's refresh — so a logged-in user who reloads after
            // the access token lapsed was bounced straight to /login despite a
            // perfectly valid refresh session. If a prior session exists
            // (token_expiry key present) but the access token has expired, try
            // exactly one refresh before giving up. A fully absent token_expiry
            // means never-logged-in / logged-out, so we skip the wasted call.
            if (!authenticated && localStorage.getItem('token_expiry') !== null) {
                try {
                    // `silent` so a failed probe on a PUBLIC route doesn't
                    // hard-redirect to /login — the route guards decide.
                    await authService.refreshToken({ silent: true });
                    authenticated = authService.isAuthenticated();
                } catch {
                    // Refresh token genuinely expired/revoked — stay
                    // unauthenticated; the route guards handle navigation.
                }
            }

            setIsAuthenticated(authenticated);

            if (authenticated) {
                setUserPhone(authService.getUserPhoneDisplay());
                // Load cached roles immediately for quick display
                const cachedRoles = authService.getCachedUserRoles();
                setUserRoles(cachedRoles);
                // Start proactive token refresh
                authService.startProactiveRefresh();
                // Then fetch fresh roles, passkeys, profile and impersonation status in parallel.
                //
                // HOF-101 (codex fix round, P1) — the impersonation read used to be gated on
                // `cachedRoles.includes('admin')`, and the cache it consulted is the one
                // `stopImpersonation` clears *because it holds the impersonated user's roles*.
                // So the second load of an impersonated session found non-admin cached roles,
                // skipped this request, and reported "not impersonating" for a session that
                // was — taking the banner off screen and handing any tenant-scoped consumer
                // the OPERATOR's identity for a customer's view. The endpoint is not
                // admin-gated (`bff-service/src/api/admin.py`): it answers `active: false`
                // for an ordinary session, which is one cheap boot request in exchange for a
                // status that is never a guess.
                const promises: Promise<unknown>[] = [
                    checkPasskeys(),
                    fetchUserRoles(),
                    fetchUserProfile(),
                    fetchImpersonationStatus(),
                ];
                await Promise.all(promises);
            }

            setIsLoading(false);
        };

        checkAuth();

        // Cleanup proactive refresh on unmount
        return () => authService.stopProactiveRefresh();
    }, [checkPasskeys, fetchUserRoles, fetchUserProfile, fetchImpersonationStatus]);

    const login = useCallback(async (phoneNumber: string, otp: string) => {
        await authService.verifyOTP(phoneNumber, otp);
        setIsAuthenticated(true);
        // Mask phone for display - verifyOTP already stores masked version in localStorage
        setUserPhone(authService.getUserPhoneDisplay());
        // Start proactive token refresh
        authService.startProactiveRefresh();
        // Fetch passkeys, roles, profile and impersonation status after login.
        //
        // HOF-101 (codex round 3, P1) — the status belongs on THIS path too, and
        // for a reason that outlives "a fresh session cannot be impersonating".
        // Since `impersonation === null` now means UNKNOWN rather than "no", a
        // login that never asks leaves it unknown for the whole session — no
        // reload follows an OTP/passkey login — and every tenant-scoped consumer
        // correctly refuses to key on that, so the preference is silently off
        // until the user happens to reload. Setting `{active: false}` from what
        // we assume about a new session would be the same shortcut that produced
        // the defect this replaced; ask, and there is one source of truth.
        const [, , profile] = await Promise.all([
            checkPasskeys(),
            fetchUserRoles(),
            fetchUserProfile(),
            fetchImpersonationStatus(),
        ]);
        return profile;
    }, [checkPasskeys, fetchUserRoles, fetchUserProfile, fetchImpersonationStatus]);

    const loginWithPasskey = useCallback(async () => {
        // Passkey authentication already completed, just update state
        setIsAuthenticated(true);
        setUserPhone(authService.getUserPhoneDisplay());
        // Start proactive token refresh
        authService.startProactiveRefresh();
        // Same as `login` above — see the note there.
        const [, , profile] = await Promise.all([
            checkPasskeys(),
            fetchUserRoles(),
            fetchUserProfile(),
            fetchImpersonationStatus(),
        ]);
        return profile;
    }, [checkPasskeys, fetchUserRoles, fetchUserProfile, fetchImpersonationStatus]);

    const logout = useCallback(async () => {
        // Stop proactive token refresh
        authService.stopProactiveRefresh();
        await authService.logout();
        // Drop every cached read with the session that filled it (HOF-93,
        // codex round 1 P1). The layouts `navigate('/')` after this — a
        // client-side route change, NOT a reload — so the module-singleton
        // QueryClient outlives the logout, and without this the next login in
        // the same tab is served the previous tenant's data while React Query
        // revalidates behind it. Cross-tenant residue is the one bug class
        // this app cannot render its way out of.
        //
        // Unconditional, and placed here rather than at the three call sites:
        // a clear that some call path can skip is not a guarantee. It is safe
        // to over-clear — every live observer simply refetches — and the module
        // import (not `useQueryClient`) is what lets AuthProvider stay mountable
        // outside a QueryClientProvider, which several suites rely on.
        queryClient.clear();
        setIsAuthenticated(false);
        setHasPasskey(false);
        setUserPhone(null);
        setUserRoles([]);
        setUserProfile(null);
        setImpersonation(null);
    }, []);

    const refreshToken = useCallback(async () => {
        await authService.refreshToken();
    }, []);

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            isLoading,
            hasPasskey,
            userPhone,
            userRoles,
            userProfile,
            impersonation,
            login,
            loginWithPasskey,
            logout,
            refreshToken,
            checkPasskeys,
            fetchUserRoles,
            fetchUserProfile,
            stopImpersonation,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
